import Card from 'v2/components/Card';
import CardActionLinkButton from 'v2/components/CardActionLinkButton';
import CardHeader from 'v2/components/CardHeader';
import ConfirmModal from 'v2/components/ConfirmModal';
import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import ExpiredPill from 'v2/components/ExpiredPill';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import RowActionsMenu from 'v2/components/RowActionsMenu';
import useCertificationsTable from 'v2/layouts/AccountWorkspaceLayout/components/CertificationsSection/hooks/useCertificationsTable';

import { getIsExpired } from '@@utils/dates';
import { getLocalDateString } from '@@utils/getLocalDateString';

import HorizontalContainer from 'components/containers/HorizontalContainer';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_LABEL,
    ACTIONS_COLUMN_WIDTH,
    CERTIFICATION_CATEGORIES_COLUMN_KEY,
    CERTIFICATION_DATE_FORMAT_OPTIONS,
    CERTIFICATION_NUMBER_COLUMN_KEY,
    DATE_LOCALE,
    EMPTY_CELL_TEXT,
    EXPIRATION_DATE_COLUMN_KEY,
    ISSUED_DATE_COLUMN_KEY,
    STATE_COLUMN_KEY,
    TEST_ID
} from './constants';
import type { DataTableColumn, DataTableRow, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CertificationsSection: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const {
        certifications,
        handleAdd,
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleEdit,
        handleLoadMore,
        hasNextPage,
        isDeleteConfirmOpen,
        isFetchingMore,
        isLoading
    } = useCertificationsTable();

    const {
        ACTIONS_LABEL,
        ADD_CERTIFICATION,
        CERTIFICATION_CATEGORIES,
        CERTIFICATION_NUMBER,
        DELETE,
        DELETE_CONFIRM_CANCEL,
        DELETE_CONFIRM_LABEL,
        DELETE_CONFIRM_MESSAGE,
        DELETE_CONFIRM_TITLE,
        EDIT,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        EXPIRATION_DATE,
        EXPIRED,
        ISSUED_DATE,
        LOAD_MORE,
        STATE,
        TABLE_LABEL,
        TITLE
    } = useTranslation();

    const columns: DataTableColumn[] = [
        { key: STATE_COLUMN_KEY, label: STATE },
        { key: CERTIFICATION_NUMBER_COLUMN_KEY, label: CERTIFICATION_NUMBER },
        { key: CERTIFICATION_CATEGORIES_COLUMN_KEY, label: CERTIFICATION_CATEGORIES },
        { key: ISSUED_DATE_COLUMN_KEY, label: ISSUED_DATE },
        { key: EXPIRATION_DATE_COLUMN_KEY, label: EXPIRATION_DATE },
        { key: ACTIONS_COLUMN_KEY, label: ACTIONS_COLUMN_LABEL, width: ACTIONS_COLUMN_WIDTH }
    ];

    const rows: DataTableRow[] = certifications.map(certification => ({
        [ACTIONS_COLUMN_KEY]: (
            <RowActionsMenu
                actions={[
                    { label: EDIT, onClick: () => handleEdit(certification.id) },
                    {
                        isDestructive: true,
                        label: DELETE,
                        onClick: () => handleDeleteRequest(certification.id)
                    }
                ]}
                ariaLabel={ACTIONS_LABEL}
            />
        ),
        [CERTIFICATION_CATEGORIES_COLUMN_KEY]:
            certification.certificationCategories || EMPTY_CELL_TEXT,
        [CERTIFICATION_NUMBER_COLUMN_KEY]:
            certification.applicatorCertificationNumber || EMPTY_CELL_TEXT,
        [EXPIRATION_DATE_COLUMN_KEY]: (
            <HorizontalContainer className={styles.expirationCell}>
                {certification.certificationExpirationDate
                    ? getLocalDateString(
                          certification.certificationExpirationDate,
                          DATE_LOCALE,
                          CERTIFICATION_DATE_FORMAT_OPTIONS
                      )
                    : EMPTY_CELL_TEXT}

                {getIsExpired(certification.certificationExpirationDate) ? (
                    <ExpiredPill text={EXPIRED} />
                ) : null}
            </HorizontalContainer>
        ),
        [ISSUED_DATE_COLUMN_KEY]: certification.issuedDate
            ? getLocalDateString(
                  certification.issuedDate,
                  DATE_LOCALE,
                  CERTIFICATION_DATE_FORMAT_OPTIONS
              )
            : EMPTY_CELL_TEXT,
        [STATE_COLUMN_KEY]: certification.stateOfCertification || EMPTY_CELL_TEXT
    }));

    return (
        <Card className={className} testId={testId}>
            <CardHeader
                action={<CardActionLinkButton onClick={handleAdd} text={ADD_CERTIFICATION} />}
                title={TITLE}
            />

            {isLoading ? <LoadingBar /> : null}

            {!isLoading && certifications.length === 0 ? (
                <EmptyState compact description={EMPTY_DESCRIPTION} heading={EMPTY_HEADING} />
            ) : null}

            {!isLoading && certifications.length > 0 ? (
                <div className={styles.tableScroll}>
                    <DataTable ariaLabel={TABLE_LABEL} columns={columns} rows={rows} />
                </div>
            ) : null}

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}

            <ConfirmModal
                cancelLabel={DELETE_CONFIRM_CANCEL}
                confirmLabel={DELETE_CONFIRM_LABEL}
                isDestructive
                isOpen={isDeleteConfirmOpen}
                message={DELETE_CONFIRM_MESSAGE}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={DELETE_CONFIRM_TITLE}
            />
        </Card>
    );
};

export default CertificationsSection;
